import { useMemo, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import type { SessionVmStatus, VmStatusResponse } from "@/lib/types";

interface Props {
  data: VmStatusResponse | null;
  error: { status?: number; message: string } | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

type Action = "start" | "stop" | "suspend" | "resume";

const rawFoundryUrl = import.meta.env.VITE_FOUNDRY_URL ?? "";
const FOUNDRY_URL = rawFoundryUrl && !rawFoundryUrl.includes("://") ? `https://${rawFoundryUrl}` : rawFoundryUrl;

function statusDot(status: SessionVmStatus): string {
  switch (status) {
    case "running":
      return "bg-emerald-400";
    case "starting":
    case "stopping":
    case "suspending":
      return "bg-amber-400 animate-pulse";
    case "suspended":
      return "bg-amber-500";
    case "error":
      return "bg-red-500";
    case "stopped":
    default:
      return "bg-slate-500";
  }
}

function statusLabel(status: SessionVmStatus): string {
  switch (status) {
    case "running":
      return "Running";
    case "starting":
      return "Starting…";
    case "stopping":
      return "Stopping…";
    case "suspending":
      return "Suspending…";
    case "suspended":
      return "Suspended";
    case "stopped":
      return "Stopped";
    case "error":
      return "Error";
  }
}

function timeAgo(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

export function VmCard({ data, error, loading, refetch }: Props) {
  const [actionBusy, setActionBusy] = useState<Action | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const notAllowed = error?.status === 403;
  const session = data?.session ?? null;
  const status: SessionVmStatus | null = data?.vm.sessionStatus ?? null;

  const busyState = useMemo<SessionVmStatus | null>(() => {
    if (actionBusy === "start") return "starting";
    if (actionBusy === "stop") return "stopping";
    if (actionBusy === "suspend") return "suspending";
    if (actionBusy === "resume") return "starting";
    return null;
  }, [actionBusy]);

  const displayStatus = busyState ?? status;

  const doAction = async (op: Action) => {
    setActionBusy(op);
    setActionError(null);
    try {
      if (op === "start") await api.start();
      else if (op === "stop") await api.stop();
      else if (op === "suspend") await api.suspend();
      else await api.resume();
      await refetch();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActionBusy(null);
    }
  };

  // Skeleton/loading
  if (loading && !data && !error) {
    return (
      <Card className="w-full max-w-sm animate-pulse">
        <CardContent className="space-y-3 p-6">
          <div className="h-4 w-1/2 rounded bg-slate-800" />
          <div className="h-3 w-3/4 rounded bg-slate-800" />
          <div className="h-9 w-full rounded bg-slate-800" />
        </CardContent>
      </Card>
    );
  }

  if (notAllowed) {
    return (
      <Card className="w-full max-w-sm">
        <CardContent className="space-y-4 p-6 text-center">
          <p className="text-sm text-slate-300">Your account isn't on the allow list.</p>
          <SignOutButton />
        </CardContent>
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card className="w-full max-w-sm">
        <CardContent className="space-y-4 p-6">
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <Button variant="outline" className="w-full" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!displayStatus) return null;

  const isTransition =
    displayStatus === "starting" ||
    displayStatus === "stopping" ||
    displayStatus === "suspending";
  const isRunning = displayStatus === "running";
  const isStopped = displayStatus === "stopped";
  const isSuspended = displayStatus === "suspended";
  const isErr = displayStatus === "error";

  return (
    <Card className="w-full max-w-sm">
      <CardContent className="space-y-4 p-6">
        {actionError && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{actionError}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setActionError(null);
                  void refetch();
                }}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${statusDot(displayStatus)}`} />
            <span className="font-mono text-sm text-slate-100">{statusLabel(displayStatus)}</span>
          </div>
          {session && isRunning && (
            <p className="font-mono text-xs text-slate-400">
              Started {timeAgo(session.startedAt)}
            </p>
          )}
          {isTransition && session && (
            <p className="font-mono text-xs text-slate-500">
              Elapsed {timeAgo(session.startedAt)}
            </p>
          )}
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          {isStopped && (
            <Button onClick={() => doAction("start")} disabled={actionBusy !== null} className="flex-1">
              Start server
            </Button>
          )}
          {isSuspended && (
            <Button onClick={() => doAction("resume")} disabled={actionBusy !== null} className="flex-1">
              Resume
            </Button>
          )}
          {isRunning && (
            <>
              <Button
                variant="destructive"
                onClick={() => doAction("stop")}
                disabled={actionBusy !== null}
                className="flex-1"
              >
                Stop server
              </Button>
              <Button
                variant="outline"
                onClick={() => doAction("suspend")}
                disabled={actionBusy !== null}
                className="flex-1"
              >
                Suspend
              </Button>
            </>
          )}
          {isTransition && (
            <Button disabled className="flex-1">
              {statusLabel(displayStatus)}
            </Button>
          )}
          {isErr && (
            <Button variant="outline" className="flex-1" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" /> Retry
            </Button>
          )}
        </div>

        {isRunning && FOUNDRY_URL && (
          <>
            <Separator />
            <a
              href={FOUNDRY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-100 hover:bg-slate-800"
            >
              Open Foundry <ExternalLink className="h-4 w-4" />
            </a>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SignOutButton() {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      className="w-full"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        await supabase.auth.signOut();
        window.location.hash = "#/login";
      }}
    >
      Sign out
    </Button>
  );
}

